import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import useSeo from "../hooks/useSeo";
import PasswordInput from "../components/PasswordInput";

/** Landing page of the emailed reset link: /reset-password?email=…&expires=…&token=… */
export default function ResetPassword() {
    const [params] = useSearchParams();
    const nav = useNavigate();
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [loading, setLoading] = useState(false);

    useSeo({
        title: "Set a New Password | IDCardGen",
        description: "Choose a new password for your IDCardGen account.",
        noindex: true,
    });

    const email = params.get("email") || "";
    const expires = params.get("expires") || "";
    const token = params.get("token") || "";
    const linkOk = email && expires && token;

    const onSubmit = async (e) => {
        e.preventDefault();
        if (password.length < 6) return toast.error("Password must be at least 6 characters.");
        if (password !== confirm) return toast.error("The two passwords do not match.");
        setLoading(true);
        try {
            await api.post("/auth/reset-password", { email, expires, token, password });
            toast.success("Password updated. Please sign in.");
            nav("/login");
        } catch (err) {
            toast.error(err.response?.data?.error || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-6" data-testid="reset-password-page">
            <div className="w-full max-w-sm space-y-5">
                <div>
                    <h1 className="font-display font-black text-3xl tracking-tighter">Set a new password</h1>
                    {linkOk && <p className="text-sm text-zinc-500 mt-1">for {email}</p>}
                </div>
                {!linkOk ? (
                    <div className="border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                        This reset link is incomplete. Please open the link from your email again, or{" "}
                        <Link to="/forgot-password" className="text-blue-600 hover:underline">
                            request a new one
                        </Link>
                        .
                    </div>
                ) : (
                    <form onSubmit={onSubmit} className="space-y-5">
                        <div>
                            <label className="label-swiss">New password</label>
                            <PasswordInput
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                autoComplete="new-password"
                                required
                                data-testid="reset-password-input"
                            />
                        </div>
                        <div>
                            <label className="label-swiss">Confirm new password</label>
                            <PasswordInput
                                value={confirm}
                                onChange={(e) => setConfirm(e.target.value)}
                                autoComplete="new-password"
                                required
                                data-testid="reset-confirm-input"
                            />
                        </div>
                        <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="reset-submit-btn">
                            {loading ? "Saving…" : "Save new password"}
                        </button>
                    </form>
                )}
                <p className="text-sm text-zinc-500">
                    Link expired?{" "}
                    <Link to="/forgot-password" className="text-blue-600 hover:underline">
                        Send a new one
                    </Link>
                </p>
            </div>
        </div>
    );
}
